import React, { useMemo } from "react";
import { Button } from "react-bootstrap";
import { XLg, PlusCircle, Clock } from "react-bootstrap-icons";
import Icon from "@mdi/react";
import { mdiArrowRight } from "@mdi/js";
import { validateAvailability } from "./validationUtils";
import { DAYS_OF_WEEK } from "./constants";

const AvailabilityEditor = ({ weeklyHours, onChange, errors }) => {
  // Live overlap detection
  const liveErrors = useMemo(
    () => validateAvailability(weeklyHours),
    [weeklyHours]
  );

  // Merge parent errors (from save) with live errors
  const mergedErrors = useMemo(() => {
    const merged = { ...liveErrors };
    if (errors) {
      for (const [day, set] of Object.entries(errors)) {
        if (merged[day]) {
          for (const idx of set) merged[day].add(idx);
        } else {
          merged[day] = new Set(set);
        }
      }
    }
    return merged;
  }, [liveErrors, errors]);

  const handleTimeChange = (day, slotIdx, field, value) => {
    const updated = { ...weeklyHours };
    const slots = [...(updated[day] || [])];
    slots[slotIdx] = { ...slots[slotIdx], [field]: value };
    updated[day] = slots;
    onChange(updated);
  };

  const handleAddSlot = (day) => {
    const updated = { ...weeklyHours };
    const slots = [...(updated[day] || [])];

    let newStart, newEnd;
    if (slots.length === 0) {
      newStart = "08:00";
      newEnd = "12:00";
    } else {
      const lastEnd = slots[slots.length - 1].end;
      const [h, m] = lastEnd.split(":").map(Number);
      const startMinutes = h * 60 + m + 60;
      const endMinutes = startMinutes + 60;
      if (startMinutes >= 24 * 60) return;
      const cappedEnd = Math.min(endMinutes, 24 * 60 - 1);
      newStart = `${String(Math.floor(startMinutes / 60)).padStart(2, "0")}:${String(startMinutes % 60).padStart(2, "0")}`;
      newEnd = `${String(Math.floor(cappedEnd / 60)).padStart(2, "0")}:${String(cappedEnd % 60).padStart(2, "0")}`;
    }

    slots.push({ start: newStart, end: newEnd });
    updated[day] = slots;
    onChange(updated);
  };

  const handleDeleteSlot = (day, slotIdx) => {
    const updated = { ...weeklyHours };
    const slots = [...(updated[day] || [])];
    slots.splice(slotIdx, 1);
    updated[day] = slots;
    onChange(updated);
  };

  return (
    <div className="booking-settings-section">
      <h4 className="booking-settings-section-title">Online Availability</h4>
      <div className="cbs-avail-list">
        {DAYS_OF_WEEK.map((day) => {
          const slots = weeklyHours[day] || [];
          const errorSet = mergedErrors[day];

          return (
            <div key={day} className="cbs-avail-day-row">
              <div className="cbs-avail-day-name">{day}</div>
              <div className="cbs-avail-day-slots">
                {slots.length === 0 ? (
                  <div className="cbs-avail-empty-row">
                    <Button
                      variant="link"
                      size="sm"
                      className="cbs-avail-add-btn"
                      onClick={() => handleAddSlot(day)}
                      aria-label={`Add time slot for ${day}`}
                    >
                      <PlusCircle size={18} />
                    </Button>
                  </div>
                ) : (
                  <>
                    {slots.map((slot, idx) => (
                      <div
                        key={idx}
                        className={`cbs-avail-slot-row${errorSet?.has(idx) ? " cbs-avail-slot-error" : ""}`}
                      >
                        <div className="cbs-avail-slot-pill">
                          <input
                            type="time"
                            className="cbs-avail-time-input"
                            value={slot.start}
                            aria-label={`${day} slot ${idx + 1} start time`}
                            onChange={(e) =>
                              handleTimeChange(day, idx, "start", e.target.value)
                            }
                          />
                          <Icon
                            path={mdiArrowRight}
                            size={0.55}
                            className="cbs-avail-arrow"
                          />
                          <input
                            type="time"
                            className="cbs-avail-time-input"
                            value={slot.end}
                            aria-label={`${day} slot ${idx + 1} end time`}
                            onChange={(e) =>
                              handleTimeChange(day, idx, "end", e.target.value)
                            }
                          />
                          <Clock size={16} className="cbs-avail-clock-icon" />
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          className="cbs-avail-delete-btn"
                          onClick={() => handleDeleteSlot(day, idx)}
                          aria-label={`Delete ${day} slot ${idx + 1}`}
                        >
                          <XLg size={14} />
                        </Button>
                        {idx === 0 ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="cbs-avail-add-btn"
                            onClick={() => handleAddSlot(day)}
                            aria-label={`Add time slot for ${day}`}
                          >
                            <PlusCircle size={18} />
                          </Button>
                        ) : (
                          <span className="cbs-avail-add-spacer" />
                        )}
                      </div>
                    ))}
                    {errorSet && errorSet.size > 0 && (
                      <div className="cbs-avail-overlap-error">
                        Periods can&apos;t overlap
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AvailabilityEditor;
